import React, { useState, useCallback } from 'react'
import { cn } from 'utils/cn'

export interface RadioGroupProps {
    label?: string
    description?: string
    error?: string
    orientation?: 'vertical' | 'horizontal'
    value?: string
    defaultValue?: string
    onChange?: (value: string) => void
    name?: string
    children: React.ReactNode
    style?: React.CSSProperties
}

export const RadioGroup = ({
    label,
    description,
    error,
    orientation = 'vertical',
    value: controlledValue,
    defaultValue,
    onChange,
    name,
    children,
    style,
}: RadioGroupProps) => {
    const [internalValue, setInternalValue] = useState(defaultValue ?? '')
    const selected = controlledValue !== undefined ? controlledValue : internalValue
    const hasError = !!error

    const handleChange = useCallback(
        (val: string) => {
            if (controlledValue === undefined) setInternalValue(val)
            onChange?.(val)
        },
        [controlledValue, onChange]
    )

    const childArray = React.Children.toArray(children).filter(Boolean)

    return (
        <fieldset role="radiogroup" className="m-0 flex flex-col gap-3 border-none p-0" style={style}>
            {(label || description) && (
                <div className="flex flex-col gap-0.5">
                    {label && <legend className="p-0 text-sm font-medium text-foreground">{label}</legend>}
                    {description && <span className="text-sm text-foreground-muted">{description}</span>}
                </div>
            )}

            <div className={cn('flex', orientation === 'vertical' ? 'flex-col gap-3' : 'flex-row flex-wrap gap-4')}>
                {childArray.map((child, i) => {
                    if (
                        React.isValidElement<{ value?: string; checked?: boolean; onChange?: (checked: boolean) => void; name?: string; invalid?: boolean }>(
                            child
                        )
                    ) {
                        const childValue = child.props.value ?? String(i)
                        return React.cloneElement(child, {
                            checked: selected === childValue,
                            onChange: () => handleChange(childValue),
                            name: name ?? 'radio-group',
                            invalid: hasError,
                        })
                    }
                    return child
                })}
            </div>

            {error && <span className="text-sm text-danger">{error}</span>}
        </fieldset>
    )
}
